<template>
  <div>
    <b-modal id="modalAttachProofs" size="lg" hide-footer hide-header centered no-stacking no-close-on-backdrop
      no-enforce-focus lazy content-class="custom-modal-content" modal-class="custom-modal-wrapper"
      @shown="onModalShown" @hidden="onModalHidden">
      <div class="modal-header !border-gray-100 p-0 px-3 pb-3 fw-medium fs-18">Attach Proofs to Job</div>

      <div class="attach-proofs-content">
        <!-- Search and Filter Section -->
        <div class="search-section">

          <!-- Search Input -->
          <div class="position-relative p-3">
            <img src="/img/search.svg" class="search position-absolute start-0 top-50 translate-middle-y"
              width="20" alt="search-icon">
            <input type="text" class="form-control fw-normal search-bar border-gray-300" placeholder="Search Proofs..." v-model="searchQuery"
              @input="onSearchInput" />
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border text-gray-400" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2 text-muted">{{ isSearching ? 'Searching proofs...' : 'Loading proofs...' }}</p>
        </div>

        <!-- No Proofs State -->
        <div v-else-if="filteredProofs.length === 0" class="text-center py-4">
          <p class="text-muted">{{ searchQuery ? 'No proofs found matching your search.' : 'No proofs available to attach.' }}</p>
        </div>

        <!-- Proof List -->
        <div v-else class="proof-list-container" @scroll="handleScroll">
          <ul class="proof-list">
            <li v-for="proof in filteredProofs"
              class="proof-list-item bg-ashore-white line-item-large cursor-pointer"
              v-bind:key="proof.id"
              @click.prevent="toggleProofSelection(proof)">
              <div class="row align-items-center">
                <!-- Proof Name -->
                <div class="col-lg-8 col-md-12 proof-status">
                  <div>
                    <div class="d-flex justify-content-start">
                      <b-form-checkbox v-model="proof.checkbox" class="proof-list-item-checkbox"
                        @change="onProofSelectionChange" @click.stop></b-form-checkbox>
                      <span class="proof-details">
                        {{ proof.name }}
                      </span>
                    </div>

                    <div class="d-flex flex-column flex-lg-row justify-content-start align-items-center mt-2 gap-12">
                      <b-badge variant="success" class="proof-status-badge">
                        <span
                          :class="`status-indicator ${getProofStatusClass(proof)}`"
                          class="rounded-pill mr-2">
                        </span>
                        {{ getProofStatusTextDisplay(proof) }}
                      </b-badge>
                      <span class="proof-info-text">{{ getVersionInfo(proof) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Avatars -->
                <div
                  class="col-lg-2 col-md-12 pr-3 d-flex align-items-center justify-content-sm-start justify-content-lg-end">
                  <doc-type-avatars 
                    :proof="proof" 
                    :proof-status-colors="proofStatusColors"
                    avatar-size="32px" />
                </div>

                <div class="col-lg-2 col-md-12 pl-3 d-flex justify-content-between align-items-center">
                  <div class="flex-fill flex-lg-grow-0">
                    <!-- View Proof Details -->
                    <a class="view-proof-details-btn w-100 my-3 mx-0" :href="getProofItemLink(proof)"
                      target="_blank" @click.stop>
                      View
                    </a>
                  </div>
                  <div class="flex-lg-fill">
                    <!-- Expand/Collapse Button -->
                    <div class="expand-button" @click.stop="toggleProofDetails(proof.id)"
                      v-if="!expandedProofs.includes(proof.id)">
                      <b-icon-chevron-down class="chevron-down"></b-icon-chevron-down>
                    </div>
                    <div class="expand-button" @click.stop="toggleProofDetails(proof.id)" v-else>
                      <b-icon-chevron-up class="chevron-up"></b-icon-chevron-up>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Expand Proof Details -->
              <div class="expand-details-section" v-if="expandedProofs.includes(proof.id)">
                <div class="row">
                  <div class="col-lg-6 proof-details-column">
                    <!-- Proof Id -->
                    <div class="col-md-12 mb-3">
                      <div class="proof-details-header-text">Proof ID</div>
                      <span class="proof-details proof-details-value">{{ proof.id }}</span>
                    </div>
                    <!-- Date Created -->
                    <div class="col-md-12 mb-3">
                      <div class="proof-details-header-text">Date Created</div>
                      <span class="proof-details proof-details-value">{{ formatLocalDate(proof.createdAt) }}</span>
                    </div>
                  </div>
                  <div class="col-lg-6 proof-details-column">
                    <!-- Owner -->
                    <div class="col-md-12 mb-3">
                      <div class="proof-details-header-text">Owner</div>
                      <span class="proof-details proof-details-value">{{ proof.currentSender }}</span>
                    </div>
                    <!-- Due Date -->
                    <div class="col-md-12 mb-3">
                      <div class="proof-details-header-text">Due Date</div>
                      <span class="proof-details proof-details-value">{{ formatLocalDate(proof.dueDate) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
          
          <!-- Load More Button -->
          <div v-if="shouldShowLoadButton" class="text-center mt-3">
            <button 
              class="btn btn-outline-secondary load-more-btn" 
              @click="loadMoreProofs"
            >
              Load More Proofs
            </button>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="modal-actions px-3 d-flex justify-content-end align-items-center">

          <!-- Action Buttons -->
          <div class="d-flex gap-2">
            <button class="cancel-btn text-ashore-black" @click="closeModal">Cancel</button>
            <button class="submit-btn bg-success-500" @click="attachSelectedProofs"
              :disabled="isSubmitDisabled"
              :class="{ 'opacity-50': isSubmitDisabled }">
              <span class="cursor-pointer">
                Submit
              </span>
            </button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { formatLocalDate, getProofStatusText, getProofStatusColor, initProofStatusColor, getProofStatusClass, getProofStatusTextDisplay, getVersionInfo } from "../../utils/helpers";
import { DOC_TYPES, PROOF_VERSION_STATUS, PROOF_STATUS_CLASS, PROOF_STATUS_TEXT, ROUTES } from "../../utils/constants";
import docTypeAvatars from "../global/doc-type-avatars.vue";

const { PROOF_ITEM } = ROUTES;

export default {
  name: 'ModalAttachProofs',
  components: {
    docTypeAvatars
  },
  props: {
    jobId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      allProofs: [],
      filteredProofs: [],
      selectedProofs: [],
      originallyAttachedProofs: [],
      pendingProofToAdd: [],
      pendingProofToRemove: [],
      isLoading: false,
      isAttaching: false,
      searchQuery: '',
      proofStatusColors: {},
      expandedProofs: [],
      searchTimeout: null,
      isSearching: false,
      DOC_TYPES,
      PROOF_VERSION_STATUS,
      PROOF_STATUS_CLASS,
      PROOF_STATUS_TEXT,
      ROUTES,
      // Pagination properties
      currentPage: 1,
      hasMoreProofs: true,
      isLoadingMore: false,
      proofsPerPage: 10,
      filter: {
        proofId: 0,
        senderId: 0,
        workflowId: 0,
        approverId: 0,
        overdue: false,
        archived: false,
        sortByDeadlineDesc: false,
        tagId: 0,
        tagFilterId: 0,
        text: "",
        status: 0
      }
    };
  },
  computed: {
    selectedProofIds() {
      return this.selectedProofs.map(proof => proof.id);
    },
    getProofStatusClass() {
      return (proof) => getProofStatusClass(proof, this.proofStatusColors);
    },
    getProofStatusTextDisplay() {
      return (proof) => getProofStatusTextDisplay(proof, this.proofStatusColors);
    },
    getVersionInfo() {
      return (proof) => getVersionInfo(proof, this.formatLocalDate);
    },
    shouldShowLoadButton() {
      return !this.isLoadingMore && this.hasMoreProofs && this.filteredProofs.length >= this.proofsPerPage;
    },
    isSubmitDisabled() {
      return (this.pendingProofToAdd.length === 0 && this.pendingProofToRemove.length === 0) || this.isAttaching;
    }
  },
  watch: {
    selectedProofs: {
      handler() {
        this.updatePendingChanges();
      },
      deep: true
    }
  },
  methods: {
    formatLocalDate,
    getProofStatusText,
    getProofStatusColor,
    initProofStatusColor,
    async onModalShown() {
      await this.loadAllProofs();
    },
    
    onModalHidden() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.resetModal();
    },
    
    resetModal() {
      this.allProofs = [];
      this.filteredProofs = [];
      this.selectedProofs = [];
      this.originallyAttachedProofs = [];
      this.pendingProofToAdd = [];
      this.pendingProofToRemove = [];
      this.searchQuery = '';
      this.proofStatusColors = {};
      this.expandedProofs = [];
      this.isLoading = false;
      this.isAttaching = false;
      // Reset pagination
      this.currentPage = 1;
      this.hasMoreProofs = true;
      this.isLoadingMore = false;
      this.isSearching = false;
      this.filter.text = "";
    },
    
    async loadAllProofs() {
      try {
        this.isLoading = true;
        this.currentPage = 1;
        this.hasMoreProofs = true;
        this.allProofs = [];
        this.filteredProofs = [];
        this.proofStatusColors = {};
        
        // Get attached proof IDs
        let attachedProofIds = [];
        try {
          const jobResp = await this.$A.JobService.getJobById(this.jobId);
          const jobProofLinks = jobResp.proofs || [];
          attachedProofIds = jobProofLinks.map(link => link.proofId);
        } catch (e) {
          console.warn('Failed to fetch job for attached proofs pre-selection', e);
        }

        // Load first page of proofs
        await this.loadProofsPage(1, attachedProofIds);
        
      } catch (error) {
        console.error('Failed to load proofs:', error);
        this.$A.AlertUser('Failed to load proofs', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async loadProofsPage(page, attachedProofIds = []) {
      if (this.isLoadingMore) return;
      
      try {
        this.isLoadingMore = true;
        
        const proofsInPage = await this.$A.ProofService.GetProofsByFilter(
          this.filter,
          page
        );
        
        if (!proofsInPage || proofsInPage.length === 0) {
          this.hasMoreProofs = false;
        } else {
          proofsInPage.forEach((proof) => {
            const isAttached = attachedProofIds.includes(proof.id);
            proof.checkbox = isAttached;
            if (isAttached) {
              this.originallyAttachedProofs.push(proof.id);
            }
            initProofStatusColor(proof, this.proofStatusColors);
            this.allProofs.push(proof);
          });
          
          this.filteredProofs = [...this.allProofs];
          this.selectedProofs = this.filteredProofs.filter(p => p.checkbox);
          this.updatePendingChanges();
        }
      } catch (error) {
        console.error('Failed to load proofs page:', error);
        this.$A.AlertUser('Failed to load proofs', 'error');
      } finally {
        this.isLoadingMore = false;
      }
    },

    async loadMoreProofs() {
      if (this.isLoadingMore || !this.hasMoreProofs) {
        return;
      }
      
      this.currentPage++;
      await this.loadProofsPage(this.currentPage);
    },

    handleScroll(event) {
      const container = event.target;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      // Load more when user scrolls to bottom (with 100px buffer)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        this.loadMoreProofs();
      }
    },
    
    onSearchInput() {
      if (this.isSearching) {
        return;
      }
      
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.performSearch();
      }, 500);
    },
    
    async performSearch() {
      if (this.isSearching) {
        return;
      }
      
      try {
        this.isSearching = true;
        this.isLoading = true;
        
        // Update filter with search query
        this.filter.text = this.searchQuery.toLowerCase();
        
        // Reset pagination and reload
        this.currentPage = 1;
        this.hasMoreProofs = true;
        this.allProofs = [];
        this.filteredProofs = [];
        this.proofStatusColors = {};
        
        // Get attached proof IDs for pre-selection
        let attachedProofIds = [];
        try {
          const jobResp = await this.$A.JobService.getJobById(this.jobId);
          const jobProofLinks = jobResp.proofs || [];
          attachedProofIds = jobProofLinks.map(link => link.proofId);
        } catch (e) {
          console.warn('Failed to fetch job for attached proofs pre-selection', e);
        }
        
        // Load first page with search filter
        await this.loadProofsPage(1, attachedProofIds);
        
      } catch (error) {
        console.error('Search failed:', error);
        this.$A.AlertUser('Search failed', 'error');
      } finally {
        this.isSearching = false;
        this.isLoading = false;
      }
    },
    
    onProofSelectionChange() {
      this.selectedProofs = this.filteredProofs.filter(proof => proof.checkbox);
    },
    
    toggleProofSelection(proof) {
      proof.checkbox = !proof.checkbox;
      this.onProofSelectionChange();
    },
    
    updatePendingChanges() {
      const currentlySelectedIds = this.selectedProofs.map(p => p.id);
      const originallyAttachedIds = this.originallyAttachedProofs;
      
      this.pendingProofToAdd = currentlySelectedIds.filter(id => !originallyAttachedIds.includes(id));
      this.pendingProofToRemove = originallyAttachedIds.filter(id => !currentlySelectedIds.includes(id));
    },
    
    toggleProofDetails(proofId) {
      const index = this.expandedProofs.indexOf(proofId);
      if (index > -1) {
        this.expandedProofs.splice(index, 1);
      } else {
        this.expandedProofs.push(proofId);
      }
    },
    
    getProofItemLink(proof) {
      return `${PROOF_ITEM}?id=${proof.id}`;
    },
    
    async attachSelectedProofs() {
      if (this.pendingProofToAdd.length === 0 && this.pendingProofToRemove.length === 0) {
        this.$A.AlertUser('No changes to save', 'info');
        return;
      }
      
      try {
        this.isAttaching = true;
        
        const proofJobLinkRequest = {
          jobId: this.jobId,
          update: this.pendingProofToAdd,
          delete: this.pendingProofToRemove
        };
        
        await this.$A.JobService.updateAndDeleteProofJobLink(proofJobLinkRequest);
        
        const addedCount = this.pendingProofToAdd.length;
        const removedCount = this.pendingProofToRemove.length;
        let message = '';
        
        if (addedCount > 0 && removedCount > 0) {
          message = `Successfully added ${addedCount} proof${addedCount !== 1 ? 's' : ''} and removed ${removedCount} proof${removedCount !== 1 ? 's' : ''}`;
        } else if (addedCount > 0) {
          message = `Successfully added ${addedCount} proof${addedCount !== 1 ? 's' : ''}`;
        } else if (removedCount > 0) {
          message = `Successfully removed ${removedCount} proof${removedCount !== 1 ? 's' : ''}`;
        }
        
        this.$A.AlertUser(message, 'success');

        this.$emit('proofs-attached', {
          added: this.pendingProofToAdd,
          removed: this.pendingProofToRemove
        });
        
        this.closeModal();
      } catch (error) {
        console.error('Failed to update proof attachments:', error);
        this.$A.AlertUser('Failed to update proof attachments', 'error');
      } finally {
        this.isAttaching = false;
      }
    },
    
    closeModal() {
      this.$bvModal.hide('modalAttachProofs');
    }
  }
};
</script>

<style scoped>
::v-deep .modal-content {
  border-radius: 8px !important;
}

.search-section {
  background-color: #F9F9F9;
}

::v-deep .modal-body {
  padding-left: 0px !important;
  padding-right: 0px !important;
}

::v-deep .bi-chevron-down,
::v-deep .bi-chevron-up {
  width: 20px !important;
  height: 20px !important;
  stroke: #111827;
}

.search-bar {
  border-radius: 6px;
  padding-left: 40px;
  background-color: #F9F9F9;
}

.cancel-btn {
    padding: 7px 11px;
    border-radius: 4px;
    border: none;
    background-color: #EEF2F5;
}

.submit-btn {
    padding: 7px 11px;
    border-radius: 4px;
    border: none;
    color: #FFFFFF;
}

.search {
  margin-left: 28px;
}

.search.top-50 {
  top: 50% !important;
}

.proof-list-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #E5E5E5;
}

.load-more-btn {
  padding: 8px 16px;
  border: 1px solid #B5B5B8;
  border-radius: 4px;
  background: white;
  color: #444444;
  font-size: 14px;
  transition: all 0.2s ease;
}

.load-more-btn:hover {
  background-color: #f8f9fa;
  border-color: #007bff;
  color: #007bff;
}

.status-indicator {
  margin-right: 4px;
  height: 6px;
  width: 6px;
}

.proof-details-column {
  padding-top: 10px;
}

.proof-details-value {
  padding-top: 8px;
}

.status-approved {
  background-color: #019256;
}

.status-not-approved {
  background-color: #EB4654;
}

.status-waiting-for-review {
  background-color: #D9B116;
}

.proof-list {
  padding-left: 0px !important;
  margin-bottom: 0px !important;
  list-style: none;
}

.proof-list-item {
  margin-bottom: 1px;
  padding: 16px 24px;
  overflow: auto;
  box-shadow: 0px 1px 3px rgb(8 11 13 / 10%), 0px 1px 2px rgb(8 11 13 / 6%);
  position: relative;
}

.expand-details-section {
  padding: 15px 0;
  margin-top: 10px;
}

.expand-button {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 20px;
}

.cursor-pointer {
  cursor: pointer !important;
}

.visible-check-group {
  display: flex;
  padding-left: 16px;
}

.proof-check-selected {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 16px;
  color: black;
}

.visible-text {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #444444;
  opacity: 1 !important;
}

.proof-status-badge {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 3px 13px;
  height: 26px;
  border: 1px solid #B5B5B8;
  border-radius: 13px;
  background-color: #F9F9F9;
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #444444;
}

.proof-info-text {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #707073;
}

.proof-details-header-text {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  color: #707073;
}

.proof-details {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #444444;
}

.view-proof-details-btn {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 9px 17px;
  height: 38px;
  background: #EEF2F5;
  box-shadow: 0px 1px 2px rgba(8, 11, 13, 0.05);
  border-radius: 6px;
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #444444;
  margin: 24px;
  text-decoration: none;
}

.view-proof-details-btn:hover {
  color: #444444;
  text-decoration: none;
  background: #E0E4E7;
}

.modal-actions {
  border-top: 1px solid #E5E5E5;
  padding-top: 20px;
}

::v-deep .proof-list-item-checkbox .custom-control-label::before {
  top: 0.2rem;
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  background: white;
  border: 1px solid #B5B5B8;
  border-radius: 4px;
  color: black;
}

::v-deep .proof-list-item-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #006F41;
}

::v-deep .proof-list-item-checkbox .custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: none;
}

@media (max-width: 500px) {
  .view-proof-details-btn {
    margin-left: 0px !important;
  }

  .proof-status {
    margin-bottom: 16px;
  }

  .expand-button {
    padding-left: 10px;
  }
}

@media (min-width: 768px) {
  .col-lg-2.d-flex {
    align-items: center;
  }
}
</style>
